import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from 'react-google-recaptcha';
import IntroVideo from '../videos/Axis_background.mp4';
import VideoComponent from './VideoComponent.js';
import PageSEO from "../components/seo/PageSEO";
import { useScrollBehavior } from './useScrollBehavior.js';
import "../styles/home.css";
import "../styles/ethics.css";

const SUBMIT_EMAIL  = "YOUR_AXIS_EMAIL@domain.com";
const RECAPTCHA_KEY = "6LdwstIpAAAAAL1_lBneLQEQRHpyUpxdO5ZXJ0R9";

const QUESTIONS = [
  {
    id: "q1",
    text: "You are asked to participate in a public community action — a cleanup, food drive, or similar — in the Axis name. The day is genuinely inconvenient. What governs your decision?",
    options: [
      { id: "a", text: "Convenience — if the timing doesn't work, it doesn't work." },
      { id: "b", text: "The obligation. I committed to regular community action when I joined this tier." },
      { id: "c", text: "Whether the action is likely to be effective." },
      { id: "d", text: "Whether enough other members will be present to make my attendance meaningful." },
    ]
  },
  {
    id: "q2",
    text: "A Practitioner in your chapter is visibly struggling — with the commitment level, the training, or the principles. What is your role as an Operator?",
    options: [
      { id: "a", text: "Let them work it out themselves — growth is their responsibility." },
      { id: "b", text: "Report the situation to leadership and let them handle it." },
      { id: "c", text: "Engage with them directly — offer honest support, practical guidance, and peer accountability." },
      { id: "d", text: "Give them space and observe whether they correct themselves over time." },
    ]
  },
  {
    id: "q3",
    text: "You are asked to publicly represent the Axis at a community event. You are personally uncomfortable with public visibility. What do you do?",
    options: [
      { id: "a", text: "Decline — personal comfort is a legitimate reason not to." },
      { id: "b", text: "Attend but minimize your visible involvement." },
      { id: "c", text: "Do it. The discomfort is mine to manage, not a reason to pass the obligation to someone else." },
      { id: "d", text: "Suggest someone more comfortable with public presence take your place." },
    ]
  },
  {
    id: "q4",
    text: "What does acting in the Axis name mean to you?",
    options: [
      { id: "a", text: "Taking any action that benefits the community." },
      { id: "b", text: "Taking actions that are explicitly authorized by Axis leadership." },
      { id: "c", text: "Acting consistently with the Sacred Three in a way that a fellow member would immediately recognize as aligned with the mission." },
      { id: "d", text: "Being visibly associated with the Axis in the community." },
    ]
  },
  {
    id: "q5",
    text: "A member of your chapter takes an action in the Axis name that you believe misrepresents the Sacred Three. What do you do?",
    options: [
      { id: "a", text: "Ignore it — they had their reasons and it is not your place to second-guess them." },
      { id: "b", text: "Report it directly to leadership without speaking to the member first." },
      { id: "c", text: "Address it directly with the member first, then through the accountability structure if needed." },
      { id: "d", text: "Publicly correct the misrepresentation in the moment." },
    ]
  },
  {
    id: "q6",
    text: "You disagree with a decision made by Axis leadership. You believe it is technically within the Codex but wrong in spirit. What do you do?",
    options: [
      { id: "a", text: "Comply without comment — the decision was made correctly by the process." },
      { id: "b", text: "Build consensus among other members before raising the issue." },
      { id: "c", text: "Raise the concern formally through the appropriate channel — then accept the outcome." },
      { id: "d", text: "Refuse participation in the specific action until the decision is revisited." },
    ]
  },
  {
    id: "q7",
    text: "An Axis action requires sustained effort over several months with no visible result. Members around you are losing motivation. What do you do?",
    options: [
      { id: "a", text: "Reassess whether the action is worth continuing and raise it with leadership." },
      { id: "b", text: "Push the group harder — motivation loss is a discipline problem." },
      { id: "c", text: "Acknowledge the difficulty honestly and maintain your own commitment visibly. Let that be the standard others can measure themselves against." },
      { id: "d", text: "Escalate to leadership and ask for direction on whether to continue." },
    ]
  },
  {
    id: "q8",
    text: "You become aware that a chapter member is behaving in their private life in ways that contradict the Sacred Three but are not illegal. What is your position?",
    options: [
      { id: "a", text: "Private behavior is private — it is not within your scope." },
      { id: "b", text: "Report it to leadership immediately." },
      { id: "c", text: "Raise it with the member directly as a peer accountability matter." },
      { id: "d", text: "Do nothing unless it directly affects chapter operations." },
    ]
  },
  {
    id: "q9",
    text: "You are leading a small group action and a member refuses a legitimate instruction. How do you handle it?",
    options: [
      { id: "a", text: "Demand compliance — authority must function or the structure breaks down." },
      { id: "b", text: "Let it go in the moment and address it afterward." },
      { id: "c", text: "Address it directly in the moment, understand the reason for refusal, and make a decision based on whether the instruction was genuinely legitimate." },
      { id: "d", text: "Remove them from the action and escalate to leadership." },
    ]
  },
  {
    id: "q10",
    text: "A community member publicly criticizes the Axis based on a clear misunderstanding of its principles. You are present. What do you do?",
    options: [
      { id: "a", text: "Ignore it — public engagement on behalf of the organization is not your role." },
      { id: "b", text: "Report it to leadership to handle." },
      { id: "c", text: "Correct the misunderstanding calmly, factually, and without escalation." },
      { id: "d", text: "Agree or say nothing to avoid confrontation." },
    ]
  },
  {
    id: "q11",
    text: "The Axis asks you to commit to a recurring monthly obligation — attendance, action, or both — that will require real sacrifice of other priorities. What is your honest response?",
    options: [
      { id: "a", text: "I will commit if the specific obligation seems reasonable." },
      { id: "b", text: "I need to understand the details fully before committing." },
      { id: "c", text: "I commit fully. I accepted these obligations when I chose this tier." },
      { id: "d", text: "I will do my best but reserve the right to adjust based on circumstances." },
    ]
  },
  {
    id: "q12",
    text: "What is the difference between community service and action taken in the Axis name?",
    options: [
      { id: "a", text: "There is no meaningful difference — helping people is helping people." },
      { id: "b", text: "Axis action is more organized and consistent than general service." },
      { id: "c", text: "Axis action is performed with the Sacred Three as the explicit framework — it is both service and statement. It is accountable to a standard that general service is not." },
      { id: "d", text: "Community service is voluntary; Axis action is obligatory." },
    ]
  },
  {
    id: "q13",
    text: "You have the opportunity to sponsor someone for membership. They are genuinely capable but their commitment to the Sacred Three is unclear to you. What do you do?",
    options: [
      { id: "a", text: "Sponsor them — capability is rare and ethical alignment can develop." },
      { id: "b", text: "Decline to sponsor them. Ethical alignment is not optional and capability without it is a liability." },
      { id: "c", text: "Sponsor them provisionally with clear expectations and accept personal accountability for their development." },
      { id: "d", text: "Pass the decision to leadership without your recommendation." },
    ]
  },
  {
    id: "q14",
    text: "What does loyalty to the Axis mean to you at this level?",
    options: [
      { id: "a", text: "Following the rules, attending events, and not speaking against the organization." },
      { id: "b", text: "Putting the Axis mission above all personal considerations without exception." },
      { id: "c", text: "Consistent action in service of the Sacred Three, holding the standard, and holding others to it — not uncritical compliance." },
      { id: "d", text: "Defending the organization and its members in all situations." },
    ]
  },
  {
    id: "q15",
    text: "A member you personally value and trust is causing harm to others within the chapter community. How does your relationship affect your response?",
    options: [
      { id: "a", text: "It does — you address it more gently because of the trust between you." },
      { id: "b", text: "You ask someone else to handle it to avoid the personal conflict." },
      { id: "c", text: "It doesn't. Accountability applies equally regardless of personal relationship." },
      { id: "d", text: "You give them additional time to self-correct before acting." },
    ]
  },
  {
    id: "q16",
    text: "You are asked to take on a leadership role within your chapter. You do not feel fully ready. What do you do?",
    options: [
      { id: "a", text: "Decline — you should not lead until you are confident you are ready." },
      { id: "b", text: "Accept and rely heavily on more experienced members to carry the weight." },
      { id: "c", text: "Accept. Readiness develops through doing, not through waiting to feel ready. Be honest about your limitations and work to close them." },
      { id: "d", text: "Negotiate a reduced scope of responsibility before accepting." },
    ]
  },
];

export default function Tier2Assessment() {
  useScrollBehavior();
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const [startTime]  = useState(Date.now());

  const [info, setInfo]                     = useState({ firstName: '', lastName: '', email: '' });
  const [answers, setAnswers]               = useState({});
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [isSubmitting, setIsSubmitting]     = useState(false);
  const [error, setError]                   = useState('');
  const [showSuccess, setShowSuccess]       = useState(false);

  function handleInfoChange(e) {
    const { name, value } = e.target;
    setInfo(prev => ({ ...prev, [name]: value }));
  }

  function handleAnswer(questionId, optionId) {
    setAnswers(prev => ({ ...prev, [questionId]: optionId }));
  }

  const answered    = Object.keys(answers).length;
  const total       = QUESTIONS.length;
  const allAnswered = answered === total;
  const progress    = Math.round((answered / total) * 100);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!info.firstName || !info.lastName || !info.email) {
      setError('Please complete your name and email before submitting.');
      return;
    }
    if (!allAnswered) {
      setError('Please answer all questions before submitting.');
      return;
    }
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA.');
      return;
    }
    if (Date.now() - startTime < 30000) {
      setError('Please take your time with the assessment.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const answerSummary = QUESTIONS.map((q, idx) => {
        const selected = q.options.find(o => o.id === answers[q.id]);
        return `Q${idx + 1}: ${q.text}\nAnswer: ${selected?.text || 'No answer'}`;
      }).join('\n\n');

      const submission = new FormData();
      submission.append('firstName', info.firstName);
      submission.append('lastName',  info.lastName);
      submission.append('email',     info.email);
      submission.append('answers',   answerSummary);
      submission.append('_subject',  `Axis Tier 2 Assessment — ${info.firstName} ${info.lastName}`);
      submission.append('_template', 'table');
      submission.append('_captcha',  'false');
      submission.append('_replyto',  info.email);
      submission.append('_honey',    '');

      const response = await fetch(
        `https://formsubmit.co/ajax/${SUBMIT_EMAIL}`,
        { method: 'POST', headers: { Accept: 'application/json' }, body: submission }
      );
      const result = await response.json();

      if (result.success === 'true' || response.ok) {
        setShowSuccess(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="home">
      <PageSEO
        title="Operator Assessment | The Axis"
        description="Axis Operator assessment."
        path="/tier-2-assessment"
        jsonLd={{ "@context": "https://schema.org", "@type": "WebPage", "name": "Axis Operator Assessment" }}
      />
      <div className="home__bg" aria-hidden="true">
        <VideoComponent videoSrc={IntroVideo} />
      </div>

      <section className="home__content">
        <div className="module et__module">

          <div className="et__header">
            <span className="about__eyebrow">Axis — Tier 2</span>
            <h1 className="home__title">Operator Assessment</h1>
            <p className="et__intro">
              This assessment evaluates your commitment to acting in the Axis name,
              your capacity for leadership and accountability, and your ethical
              orientation within the group and the broader community.
            </p>
            <p className="et__intro">
              Answer honestly. These questions have no correct answers that are
              obvious — they are designed to surface how you actually think.
            </p>
          </div>

          <form className="et__form" onSubmit={handleSubmit} noValidate>

            <div className="et__section">
              <h2 className="et__section-heading">Your Details</h2>
              <div className="et__info-grid">
                <div className="et__field">
                  <label className="et__label" htmlFor="et-first">First Name</label>
                  <input id="et-first" className="et__input" type="text" name="firstName"
                    value={info.firstName} onChange={handleInfoChange} autoComplete="given-name" required />
                </div>
                <div className="et__field">
                  <label className="et__label" htmlFor="et-last">Last Name</label>
                  <input id="et-last" className="et__input" type="text" name="lastName"
                    value={info.lastName} onChange={handleInfoChange} autoComplete="family-name" required />
                </div>
                <div className="et__field et__field--full">
                  <label className="et__label" htmlFor="et-email">Email</label>
                  <input id="et-email" className="et__input" type="email" name="email"
                    value={info.email} onChange={handleInfoChange} autoComplete="email" required />
                </div>
              </div>
            </div>

            <div className="et__progress-wrap">
              <div className="et__progress-bar">
                <div className="et__progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <span className="et__progress-label">{answered} of {total} answered</span>
            </div>

            <div className="et__questions">
              {QUESTIONS.map((q, idx) => (
                <div key={q.id} className={`et__question ${answers[q.id] ? 'et__question--answered' : ''}`} id={q.id}>
                  <div className="et__question-header">
                    <span className="et__question-number">{String(idx + 1).padStart(2, '0')}</span>
                    <p className="et__question-text">{q.text}</p>
                  </div>
                  <div className="et__options">
                    {q.options.map(opt => (
                      <label key={opt.id} className={`et__option ${answers[q.id] === opt.id ? 'et__option--selected' : ''}`}>
                        <input type="radio" name={q.id} value={opt.id}
                          checked={answers[q.id] === opt.id}
                          onChange={() => handleAnswer(q.id, opt.id)}
                          className="et__radio" />
                        <span className="et__option-marker">{opt.id.toUpperCase()}</span>
                        <span className="et__option-text">{opt.text}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="et__submit-section">
              {error && <p className="et__error">{error}</p>}
              <div className="et__captcha">
                <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_KEY}
                  onChange={token => setRecaptchaToken(token || '')} theme="dark" />
              </div>
              <button className="btn btn--primary et__submit-btn" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting…" : "Submit Assessment"}
              </button>
              <p className="et__fine-print">
                Your responses are submitted directly to Axis leadership for review.
                You will be contacted at the email address provided.
              </p>
            </div>

          </form>
        </div>
      </section>

      {showSuccess && (
        <div className="et__success-overlay">
          <div className="et__success-box">
            <div className="et__success-icon" aria-hidden="true">
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2"/>
                <path d="M15 24l7 7 11-12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="jm__eyebrow">Assessment Received</span>
            <h2 className="et__success-title">Submitted</h2>
            <p className="et__success-body">
              Your assessment has been received. Axis leadership will review
              your responses and be in touch at the email address provided.
            </p>
            <p className="et__success-mantra">
              Reverence is the why. Power is the can. Duty is the must.
            </p>
            <button className="btn btn--primary" onClick={() => navigate('/')}>
              Return Home
            </button>
          </div>
        </div>
      )}
    </main>
  );
}